import { useEffect, useState } from 'react';
import { api, formatMoney, mediaUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { CreditCard, XCircle, RefreshCcw, Star, Package, CheckCircle2, Clock, AlertCircle, Truck, Handshake, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { TrackingModal } from '@/components/marketplace/ShippingModals';
import { OrderShipmentPanel } from '@/components/marketplace/OrderShipmentPanel';
import { useAuth } from '@/context/AuthContext';

const statusLabels = {
    pending_payment: { label: 'Pendiente de pago', variant: 'outline', color: 'text-amber-600', icon: Clock },
    paid: { label: 'Pagado', variant: 'secondary', color: 'text-blue-600', icon: CheckCircle2 },
    delivered: { label: 'Entregado', variant: 'secondary', color: 'text-emerald-600', icon: Package },
    released: { label: 'Completado', variant: 'default', color: 'text-emerald-700', icon: CheckCircle2 },
    refunded: { label: 'Reembolsado', variant: 'destructive', color: 'text-red-600', icon: RefreshCcw },
    cancelled: { label: 'Cancelado', variant: 'destructive', color: 'text-neutral-500', icon: XCircle },
};

export default function PurchasesPage() {
    const { user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rateOrder, setRateOrder] = useState(null);
    const [stars, setStars] = useState(5);
    const [comment, setComment] = useState('');

    const [refundOrder, setRefundOrder] = useState(null);
    const [refundReason, setRefundReason] = useState('');

    const [cancelOrder, setCancelOrder] = useState(null);
    const [trackOrder, setTrackOrder] = useState(null);

    const [filterStatus, setFilterStatus] = useState('all');
    const [search, setSearch] = useState('');

    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get('/me/orders/purchases'); setOrders(data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const filtered = orders.filter(o => {
        if (filterStatus !== 'all' && o.status !== filterStatus) return false;
        if (search) {
            const q = search.toLowerCase();
            const title = (o.ad_snapshot?.title || '').toLowerCase();
            const seller = (o.seller?.nickname || '').toLowerCase();
            if (!title.includes(q) && !seller.includes(q)) return false;
        }
        return true;
    });

    const confirmReceived = async (id) => {
        try { await api.post(`/orders/${id}/confirm-received`); toast.success('Recepción confirmada. Pago liberado al vendedor.'); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const continuePayment = async (id) => {
        try {
            const { data } = await api.post(`/orders/${id}/resume-checkout`, { origin_url: window.location.origin });
            if (data.url) window.location.href = data.url;
        } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo reanudar el pago'); }
    };

    const doCancel = async () => {
        if (!cancelOrder) return;
        try {
            await api.post(`/orders/${cancelOrder.id}/cancel`);
            toast.success('Compra cancelada');
            setCancelOrder(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const doRequestRefund = async () => {
        if (!refundOrder) return;
        if (refundReason.trim().length < 3) { toast.error('Ingresa un motivo (mín 3 caracteres)'); return; }
        try {
            await api.post(`/orders/${refundOrder.id}/request-refund`, { reason: refundReason.trim() });
            toast.success('Solicitud de reembolso enviada al vendedor');
            setRefundOrder(null); setRefundReason('');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const submitRating = async () => {
        try {
            await api.post('/ratings', { order_id: rateOrder.id, ratee_id: rateOrder.seller_id, stars, comment });
            toast.success('Calificación enviada');
            setRateOrder(null); setStars(5); setComment('');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    // Summary metrics
    const totalPurchases = orders.length;
    const totalSpent = orders.filter(o => ['paid', 'delivered', 'released'].includes(o.status)).reduce((s, o) => s + Number(o.total || 0), 0);
    const pendingCount = orders.filter(o => o.status === 'pending_payment').length;
    const completedCount = orders.filter(o => o.status === 'released').length;
    const currency = orders[0]?.currency?.toUpperCase() || 'MXN';

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Mis compras</h1>
                <Button variant="outline" size="sm" onClick={load} data-testid="purchases-reload"><RefreshCcw className="h-4 w-4 mr-1" />Actualizar</Button>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-xl border bg-card p-3" data-testid="purchases-stat-total">
                    <div className="text-xs text-muted-foreground">Total compras</div>
                    <div className="text-xl sm:text-2xl font-bold mt-1">{totalPurchases}</div>
                </div>
                <div className="rounded-xl border bg-card p-3" data-testid="purchases-stat-spent">
                    <div className="text-xs text-muted-foreground">Total gastado</div>
                    <div className="text-lg sm:text-2xl font-bold mt-1 text-red-600">{formatMoney(totalSpent, currency)}</div>
                </div>
                <div className="rounded-xl border bg-card p-3" data-testid="purchases-stat-pending">
                    <div className="text-xs text-muted-foreground">Pendientes de pago</div>
                    <div className="text-xl sm:text-2xl font-bold mt-1 text-amber-600">{pendingCount}</div>
                </div>
                <div className="rounded-xl border bg-card p-3" data-testid="purchases-stat-completed">
                    <div className="text-xs text-muted-foreground">Completadas</div>
                    <div className="text-xl sm:text-2xl font-bold mt-1 text-emerald-600">{completedCount}</div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-2">
                <Input
                    placeholder="Buscar por producto o vendedor..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="sm:max-w-xs"
                    data-testid="purchases-search"
                />
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="sm:w-48" data-testid="purchases-filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos los estados</SelectItem>
                        <SelectItem value="pending_payment">Pendiente de pago</SelectItem>
                        <SelectItem value="paid">Pagado</SelectItem>
                        <SelectItem value="delivered">Entregado</SelectItem>
                        <SelectItem value="released">Completado</SelectItem>
                        <SelectItem value="refunded">Reembolsado</SelectItem>
                        <SelectItem value="cancelled">Cancelado</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground">Cargando...</div>
            ) : filtered.length === 0 ? (
                <div className="rounded-2xl border border-dashed bg-card p-10 text-center">
                    <Package className="h-10 w-10 mx-auto text-muted-foreground" />
                    <div className="mt-3 text-sm text-muted-foreground">
                        {orders.length === 0 ? 'Aún no tienes compras.' : 'No hay compras con los filtros aplicados.'}
                    </div>
                </div>
            ) : (
                <div className="space-y-3" data-testid="purchases-list">
                    {filtered.map(o => {
                        const s = statusLabels[o.status] || { label: o.status, variant: 'outline', color: '', icon: AlertCircle };
                        const StatusIcon = s.icon;
                        const rr = o.refund_request;
                        return (
                            <div key={o.id} className="rounded-2xl border bg-card p-3 sm:p-4" data-testid={`purchase-${o.id}`}>
                                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                                    <div className="h-20 w-20 rounded-lg overflow-hidden bg-[hsl(var(--ok-surface-2))] shrink-0">
                                        {o.ad_snapshot?.media?.[0]?.url ?
                                            <img src={mediaUrl(o.ad_snapshot.media[0].url)} alt="" className="h-full w-full object-cover" /> :
                                            <div className="h-full w-full flex items-center justify-center text-xs text-muted-foreground">Sin foto</div>}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Badge variant={s.variant} className="gap-1"><StatusIcon className="h-3 w-3" />{s.label}</Badge>
                                            {rr?.status === 'pending' && <Badge variant="outline" className="border-amber-500 text-amber-700">Reembolso solicitado</Badge>}
                                            {rr?.status === 'rejected' && <Badge variant="outline" className="border-red-500 text-red-700">Reembolso rechazado</Badge>}
                                            <span className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString('es-MX')}</span>
                                        </div>
                                        <div className="font-medium mt-1 truncate">{o.ad_snapshot?.title}</div>
                                        <div className="text-xs text-muted-foreground">Vendedor: @{o.seller?.nickname}</div>
                                        <div className="money mt-1">{formatMoney(o.total, o.currency?.toUpperCase())}</div>
                                        <div className="text-[11px] text-muted-foreground">Incluye comisión {formatMoney(o.commission_amount, o.currency?.toUpperCase())}</div>
                                        {rr?.status === 'rejected' && rr.rejection_reason && (
                                            <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">Motivo del rechazo: {rr.rejection_reason}</div>
                                        )}
                                    </div>
                                    <div className="flex flex-row sm:flex-col gap-2 items-stretch sm:items-end flex-wrap">
                                        {o.status === 'pending_payment' && (
                                            <>
                                                <Button size="sm" onClick={() => continuePayment(o.id)} className="bg-red-600 hover:bg-red-700" data-testid={`continue-payment-${o.id}`}>
                                                    <CreditCard className="h-4 w-4 mr-1" />Continuar pago
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => setCancelOrder(o)} data-testid={`cancel-order-${o.id}`}>
                                                    <XCircle className="h-4 w-4 mr-1" />Cancelar
                                                </Button>
                                            </>
                                        )}
                                        {['paid', 'delivered'].includes(o.status) && (
                                            <>
                                                {o.delivery_method === 'in_person' ? (
                                                    <div className="space-y-1.5">
                                                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 flex items-start gap-1.5 text-[11px] text-amber-800">
                                                            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                                            <span><strong>Entrega en persona.</strong> Libera el pago solo al recibir el producto. <strong>Okgo no se hace responsable de la liberación del pago una vez que el comprador haya decidido liberar el pago.</strong></span>
                                                        </div>
                                                        <Button size="sm" onClick={() => confirmReceived(o.id)} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid={`release-inperson-${o.id}`}>
                                                            <Handshake className="h-4 w-4 mr-1" />Liberar pago al recibir
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        {o.shipping?.tracking_number && (
                                                            <Button size="sm" variant="outline" onClick={() => setTrackOrder(o)} data-testid={`track-${o.id}`}>
                                                                <Truck className="h-4 w-4 mr-1" />Rastrear envío
                                                            </Button>
                                                        )}
                                                        <Button size="sm" onClick={() => confirmReceived(o.id)} data-testid={`confirm-received-${o.id}`}>
                                                            <CheckCircle2 className="h-4 w-4 mr-1" />Confirmar recepción
                                                        </Button>
                                                    </>
                                                )}
                                                {!rr && (
                                                    <Button size="sm" variant="outline" onClick={() => setRefundOrder(o)} data-testid={`request-refund-${o.id}`}>
                                                        <RefreshCcw className="h-4 w-4 mr-1" />Solicitar reembolso
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                        {o.status === 'released' && (
                                            <Button size="sm" variant="outline" onClick={() => setRateOrder(o)} data-testid={`rate-seller-${o.id}`}>
                                                <Star className="h-4 w-4 mr-1" />Calificar
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                {['paid', 'delivered', 'released'].includes(o.status) && (
                                    <div className="mt-3 border-t pt-3">
                                        <OrderShipmentPanel order={o} currentUserId={user?.id} onUpdated={load} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Rating dialog */}
            <Dialog open={!!rateOrder} onOpenChange={() => setRateOrder(null)}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md">
                    <DialogHeader><DialogTitle>Calificar vendedor</DialogTitle>
                        <DialogDescription>Comparte tu experiencia con @{rateOrder?.seller?.nickname}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map(n => (
                                <button key={n} onClick={() => setStars(n)} className={`h-10 w-10 text-2xl transition-transform hover:scale-110 ${n <= stars ? 'text-amber-500' : 'text-neutral-300'}`} data-testid={`rate-star-${n}`}>★</button>
                            ))}
                            <span className="text-sm text-muted-foreground ml-2">{stars}/5</span>
                        </div>
                        <Textarea placeholder="Comentario (opcional)" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="rate-comment" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRateOrder(null)}>Cancelar</Button>
                        <Button onClick={submitRating} className="bg-red-600 hover:bg-red-700" data-testid="submit-rating">Enviar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Refund request dialog */}
            <Dialog open={!!refundOrder} onOpenChange={() => { setRefundOrder(null); setRefundReason(''); }}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Solicitar reembolso</DialogTitle>
                        <DialogDescription>El vendedor recibirá tu solicitud y podrá aprobarla o rechazarla.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="text-sm">Producto: <span className="font-medium">{refundOrder?.ad_snapshot?.title}</span></div>
                        <div className="text-sm">Monto: <span className="font-medium text-red-600">{refundOrder && formatMoney(refundOrder.total, refundOrder.currency?.toUpperCase())}</span></div>
                        <Textarea
                            placeholder="Explica el motivo del reembolso (mínimo 3 caracteres)..."
                            value={refundReason}
                            onChange={(e) => setRefundReason(e.target.value)}
                            rows={4}
                            data-testid="refund-reason"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setRefundOrder(null); setRefundReason(''); }}>Cancelar</Button>
                        <Button onClick={doRequestRefund} className="bg-red-600 hover:bg-red-700" data-testid="submit-refund">Enviar solicitud</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cancel confirmation dialog */}
            <Dialog open={!!cancelOrder} onOpenChange={() => setCancelOrder(null)}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Cancelar compra</DialogTitle>
                        <DialogDescription>¿Seguro que quieres cancelar esta compra pendiente de pago? Esta acción no se puede deshacer.</DialogDescription>
                    </DialogHeader>
                    <div className="text-sm">{cancelOrder?.ad_snapshot?.title}</div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCancelOrder(null)}>Mantener</Button>
                        <Button variant="destructive" onClick={doCancel} data-testid="submit-cancel">Cancelar compra</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <TrackingModal open={!!trackOrder} onOpenChange={() => setTrackOrder(null)} order={trackOrder} />
        </div>
    );
}
